/** @format */
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {restrictToHorizontalAxis} from '@dnd-kit/modifiers'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {flexRender, type Header} from '@tanstack/react-table'
import clsx from 'clsx'
import React, {useMemo, type MouseEvent, type PropsWithChildren} from 'react'

import {useFormApi} from '@src/contexts'
import * as toast from '@src/toast'

import {RESERVED_FIELDS} from './constants'
import {SelectionCell} from './SelectionCell'
import {StatusCell} from './StatusCell'
import type {Submission} from './SubmissionsContext'
import {useTableModel} from './TableModel'
import {getColumnSizeVar} from './utils'

export function DragAndDropTableHeaderProvider(props: PropsWithChildren) {
  const {children} = props
  const {updateDisplayConfig} = useFormApi()
  const {columnOrder, setColumnOrder} = useTableModel()

  const sensors = useSensors(
    useSensor(KeyboardSensor, {}),
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
  )

  async function handleDragEnd(event: DragEndEvent): Promise<void> {
    const {active, over} = event
    if (!over || active.id === over.id) {
      return
    }

    const original = columnOrder
    const source = original.indexOf(`${active.id}`)
    const target = original.indexOf(`${over.id}`)
    if (source === -1 || target === -1) {
      return
    }

    try {
      const updated = arrayMove(columnOrder, source, target)
      setColumnOrder(updated)

      const order = updated.filter(f => !RESERVED_FIELDS.has(f))
      await updateDisplayConfig({order})
    } catch (err) {
      setColumnOrder(original)
      toast.error('Unable to update column order.')
    }
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext
        items={columnOrder}
        strategy={horizontalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  )
}

type TableHeaderProps = {
  header: Header<Submission, unknown>
}

export function TableHeader(props: TableHeaderProps) {
  const {header} = props
  const {id} = header
  switch (id) {
    case '_selection':
      return (
        <SelectionCell className="border-b">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </SelectionCell>
      )
    case '_status':
      return (
        <StatusCell className="border-b">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </StatusCell>
      )
    case '_date':
      return <StaticTableHeader header={header} />
    default:
      return <DraggableTableHeader header={header} />
  }
}

function DraggableTableHeader(props: TableHeaderProps) {
  const {header} = props
  const canResize = header.column.getCanResize()
  const isLastColumn = header.column.getIsLastColumn()

  const {attributes, isDragging, listeners, setNodeRef, transform} =
    useSortable({id: header.id})

  // Memoize to optimize perf during dragging
  const buttonContent = useMemo(
    () => (
      <span className="whitespace-nowrap text-xs font-medium text-gray-700">
        {flexRender(header.column.columnDef.header, header.getContext())}
      </span>
    ),
    [header],
  )

  // Memoize to optimize perf during dragging
  const resizeHandle = useMemo(() => {
    return (
      canResize && (
        <ResizeHandle
          isLastColumn={isLastColumn}
          onResize={header.getResizeHandler()}
        />
      )
    )
  }, [canResize, header, isLastColumn])

  return (
    <div
      className={clsx(
        'relative flex-none border-b border-gray-100',
        isLastColumn ? 'grow' : 'border-e',
      )}
      style={{
        width: `var(${getColumnSizeVar(header.id)})`,
      }}
    >
      <button
        {...attributes}
        {...listeners}
        className="flex w-full cursor-grab justify-center px-4 py-2 data-[state=dragging]:z-[1] data-[state=dragging]:cursor-grabbing data-[state=dragging]:opacity-70 data-[state=dragging]:shadow-lg"
        data-state={isDragging ? 'dragging' : null}
        ref={setNodeRef}
        style={{
          transform: CSS.Translate.toString(transform),
        }}
        type="button"
      >
        {buttonContent}
      </button>
      {resizeHandle}
    </div>
  )
}

type ResizeHandleProps = {
  isLastColumn: boolean
  onResize: (event: unknown) => void
}

function ResizeHandle(props: ResizeHandleProps) {
  const {isLastColumn, onResize} = props
  const {current: currentForm, updateDisplayConfig} = useFormApi()
  const {table} = useTableModel()

  function handleMouseDown(event: MouseEvent<HTMLButtonElement>) {
    onResize(event)

    // Attach `mousedown` and `mouseup` event listeners on `window` rather than
    // passing `onMouseUp={handleResizeEnd}`.
    //
    // These are needed to handle cases when the mouse is dragged outside
    // the component's bounds.
    window.addEventListener('mousedown', onResize)
    window.addEventListener('mouseup', handleResizeEnd)
  }

  async function handleResizeEnd() {
    window.removeEventListener('mousedown', onResize)
    window.removeEventListener('mouseup', handleResizeEnd)

    const {columnSizing} = table.getState()
    const sizes = {
      ...currentForm.display_config?.sizes,
      ...columnSizing,
    }
    await updateDisplayConfig({sizes})
  }

  return (
    <button
      className={clsx(
        'absolute inset-y-0 z-[1] cursor-ew-resize',
        isLastColumn ? 'end-0 w-3' : '-end-2 w-4',
      )}
      onMouseDown={handleMouseDown}
      onTouchStart={onResize}
      onTouchEnd={handleResizeEnd}
    />
  )
}

function StaticTableHeader(props: TableHeaderProps) {
  const {header} = props
  const canResize = header.column.getCanResize()
  const isLastColumn = header.column.getIsLastColumn()
  return (
    <div
      className={clsx(
        'relative flex flex-none justify-center border-b border-gray-100 px-4 py-2',
        isLastColumn ? 'grow' : 'border-e',
      )}
      style={{
        width: `var(${getColumnSizeVar(header.id)})`,
      }}
    >
      <span className="whitespace-nowrap text-xs font-medium text-gray-700">
        {flexRender(header.column.columnDef.header, header.getContext())}
      </span>
      {canResize && (
        <ResizeHandle
          isLastColumn={isLastColumn}
          onResize={header.getResizeHandler()}
        />
      )}
    </div>
  )
}
